/**
 * Discoveries.js
 *
 * Phase 4 (June 2026) — full-page "trophy room" at /discoveries listing every
 * legendary world the player was the FIRST to scan into the registry, with its
 * archetype badge and AI discovery citation.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Gem, Sparkles, CircleDot, Flame, Crown, ArrowLeft, Telescope } from 'lucide-react';

const THEME = {
  crystalline: { Icon: Gem, glow: '34,211,238', from: '#0e7490', accent: 'text-cyan-200' },
  bioluminescent: { Icon: Sparkles, glow: '52,211,153', from: '#047857', accent: 'text-emerald-200' },
  ringed: { Icon: CircleDot, glow: '196,181,253', from: '#6d28d9', accent: 'text-violet-200' },
  volcanic: { Icon: Flame, glow: '251,146,60', from: '#c2410c', accent: 'text-orange-200' },
};

function Discoveries() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [discoveries, setDiscoveries] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/legendary/discoveries');
      setDiscoveries(data?.discoveries || []);
    } catch (_e) {
      setDiscoveries([]);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const items = discoveries || [];

  return (
    <div className="min-h-screen">
      <Header breadcrumbs={[{ label: 'Discoveries' }]} />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Crown className="w-7 h-7 text-yellow-300" />
            <div>
              <h1 className="text-3xl font-bold text-primary" data-testid="discoveries-title">Legendary Discoveries</h1>
              <p className="text-sm text-muted-foreground">Wonder worlds you were the first to chart.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/explore/octants')} data-testid="discoveries-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Explore
          </Button>
        </div>

        {discoveries === null ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border border-cyan-400/15 rounded-xl bg-white/[0.02]" data-testid="discoveries-empty">
            <Telescope className="w-12 h-12 mx-auto text-cyan-400/40 mb-4" />
            <p className="text-lg text-slate-300">No legendary worlds discovered yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              Only ~1 in 60 bodies is a wonder. Scan planets &amp; moons across the universe to find — and claim — one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="discoveries-grid">
            {items.map((d) => {
              const theme = THEME[d.archetype] || THEME.crystalline;
              const { Icon } = theme;
              return (
                <div
                  key={d.body_key}
                  className="relative overflow-hidden rounded-xl border border-white/10 p-5"
                  style={{
                    background: `linear-gradient(135deg, ${theme.from}33, rgba(8,11,18,0.7))`,
                    boxShadow: `0 0 28px rgba(${theme.glow},0.2)`,
                  }}
                  data-testid={`discovery-${d.body_key}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center border border-white/15"
                      style={{ background: `rgba(${theme.glow},0.14)` }}
                    >
                      <Icon className={`w-7 h-7 ${theme.accent}`} />
                    </div>
                    <div>
                      <div className={`text-lg font-bold ${theme.accent}`}>{d.body_name}</div>
                      <div className="text-xs text-slate-300/80">{d.archetype_name} · {d.body_type}</div>
                    </div>
                  </div>
                  {d.lore && <p className="mt-3 text-sm italic text-slate-200/85 leading-relaxed">“{d.lore}”</p>}
                  <div className="mt-3 text-[11px] text-slate-400">
                    {d.coords_key} · Discovered {new Date(d.discovered_at).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Discoveries;
